/**
 * ChatPanel.jsx — pure conversation view for the Underwriting Workspace.
 *
 * Renders interleaved (by timestamp) tool narrations + user/assistant chat
 * turns. The actual tool buttons + funnel stages live in FunnelSidebar.jsx.
 * This panel is JUST the conversation + the chat input.
 */
import { useEffect, useRef, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  Send,
  Sparkles,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";


export default function ChatPanel({
  session,            // {tool_history, chat_history}
  chatBusy,
  orchestratorBusy,
  onSendMessage,
  emptyPlaceholder,
}) {
  const [chatInput, setChatInput]   = useState("");
  const [expanded, setExpanded]     = useState(null);
  const messagesRef                 = useRef(null);

  // Merge + sort by timestamp
  const messages = useMemo(() => {
    const out = [];
    for (const t of (session?.tool_history || [])) {
      out.push({
        role:      "assistant",
        kind:      "tool_narration",
        stage:     t.stage,
        content:   t.narration || "",
        result:    t.result,
        timestamp: t.timestamp || "",
      });
    }
    for (const m of (session?.chat_history || [])) {
      out.push({
        role:      m.role,
        kind:      "chat",
        content:   m.content,
        timestamp: m.timestamp || "",
      });
    }
    out.sort((a, b) => String(a.timestamp).localeCompare(String(b.timestamp)));
    return out;
  }, [session]);

  // Auto-scroll on new content
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages.length, chatBusy, orchestratorBusy]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = chatInput.trim();
    if (!msg || chatBusy) return;
    setChatInput("");
    onSendMessage(msg);
  };

  return (
    <div className="flex flex-col h-full" data-testid="uw-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-1 pb-3 border-b mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-600" />
          <span className="font-semibold">Underwriting Copilot</span>
          <Badge variant="secondary" className="text-[10px]">sf-uw-service · Sonnet 4.5</Badge>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={messagesRef}
        className="flex-1 overflow-auto space-y-3 px-1 pb-3"
        data-testid="uw-chat-messages"
      >
        {messages.length === 0 && !orchestratorBusy && (
          <div className="text-center text-sm text-muted-foreground py-12">
            <Sparkles className="w-7 h-7 mx-auto mb-3 text-violet-300" />
            {emptyPlaceholder || (
              <>
                Click <strong>Start analysis</strong> on the left to begin.
                <p className="text-xs mt-3">
                  The bot will work through eligibility, affordability, credit, network, and KYC —
                  pausing only when it needs your decision.
                </p>
              </>
            )}
          </div>
        )}

        {messages.map((m, idx) => {
          const isUser = m.role === "user";
          const isExpanded = expanded === idx;
          return (
            <div
              key={idx}
              className={`flex ${isUser ? "justify-end" : "justify-start"}`}
              data-testid={`uw-chat-msg-${idx}`}
            >
              <div
                className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
                  isUser ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-900"
                }`}
              >
                {m.kind === "tool_narration" && (
                  <Badge variant="outline" className="mb-1 text-[10px] bg-white">
                    {m.stage}
                  </Badge>
                )}
                <div className="whitespace-pre-wrap leading-snug">{m.content}</div>
                {m.kind === "tool_narration" && m.result && (
                  <>
                    <button
                      onClick={() => setExpanded(isExpanded ? null : idx)}
                      className="mt-2 text-[10px] flex items-center gap-1 text-slate-500 hover:text-slate-700"
                      data-testid={`uw-chat-raw-${idx}`}
                    >
                      {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      {isExpanded ? "Hide raw" : "View raw"}
                    </button>
                    {isExpanded && (
                      <pre className="mt-2 text-[10px] bg-white/80 rounded p-2 max-h-60 overflow-auto border border-slate-200">
                        {JSON.stringify(m.result, null, 2)}
                      </pre>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}

        {(chatBusy || orchestratorBusy) && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-lg px-3 py-2 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {orchestratorBusy ? "Working through the assessment…" : "Thinking…"}
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex gap-2 border-t pt-3" data-testid="uw-chat-form">
        <Input
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="Ask, justify a fail, or paste extra context…"
          disabled={chatBusy || orchestratorBusy}
          data-testid="uw-chat-input"
        />
        <Button type="submit" size="sm" disabled={chatBusy || orchestratorBusy || !chatInput.trim()} data-testid="uw-chat-send">
          {chatBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>

      <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
        <AlertTriangle className="w-3 h-3" />
        Copilot makes no credit decision — every claim is grounded in a real tool run.
      </div>
    </div>
  );
}
